import PropTypes from "prop-types"
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { ExportAction, SendMessageAction } from "src/actions/reservations"
import { useMarina } from "src/contexts/MarinaContext"
import StatCard from "src/main/Manage/NightView/StatCard"

import BoatPreviewLink from "src/components/BoatPreviewLink"
import Chips from "src/components/Chips"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import InlineNotice from "src/components/InlineNotice"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import {
  exportNightReservations,
  getNightDatatable,
  getNightSelectableIds,
  printNightReservations,
} from "src/api/Manage/Reservations"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatDimension } from "src/utils/UnitConversion"
import { titlecase } from "src/utils/string_helpers"

import BulkMessageModal from "./BulkMessageModal"
import ReservationBadges from "./ReservationBadges"
import SpaceAssignmentCell from "./SpaceAssignmentCell"
import { formatDate } from "./helpers"

export const BOOKING_TYPE_CONTRACT = "contract"
export const BOOKING_TYPE_RESERVATION = "reservation"

const DEFAULT_FILTERS = {
  search: "",
  bookingType: "",
  spaceGroups: [],
  unassigned: false,
  arrival: false,
  departure: false,
  insurance: [],
}

const renderInsuranceOrRegistrationStatusIcon = (status) => {
  switch (status) {
    case "active":
      return (
        <div className="w-min">
          <Tooltip text="Active" placement="left" variant="dark">
            <i className="fa-kit fa-check text-lg text-teal-600" />
          </Tooltip>
        </div>
      )
    case "expiring_soon":
      return (
        <div className="w-min">
          <Tooltip text="Expiring Soon" placement="left" variant="dark">
            <i className="fa-kit fa-exclamation-circle text-lg font-semibold text-yellow-600" />
          </Tooltip>
        </div>
      )
    case "expired":
      return (
        <div className="w-min">
          <Tooltip text="Expired" placement="left" variant="dark">
            <i className="fa-kit fa-exclamation-circle text-lg font-semibold text-red-600" />
          </Tooltip>
        </div>
      )
    case "missing":
      return (
        <div className="w-min">
          <Tooltip text="Missing" placement="left" variant="dark">
            <i className="fa-kit fa-question-circle-btl text-lg font-bold text-gray-500" />
          </Tooltip>
        </div>
      )
    default:
      return null
  }
}

const ReservationNightTable = ({
  night,
  showingTonightsBoats,
  marinaName,
  marinaMcomId,
  bulkMessagingEnabled,
  paths,
  i18n,
  counts,
  countsLoading,
  activeStatCard,
  onActiveStatCardChange,
  filters,
  onFiltersChange,
}) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const tracker = useTracker()

  const isControlledFilters =
    filters !== undefined && onFiltersChange !== undefined

  const datatableFilterConfig = isControlledFilters
    ? {
        filters: {
          search: filters.search ?? "",
          bookingType: filters.bookingType ?? "",
          spaceGroups: filters.selectedSpaceGroups ?? [],
          unassigned: filters.showUnassigned ?? false,
          arrival: filters.arrival ?? false,
          departure: filters.departure ?? false,
          insurance: filters.insurance ?? [],
        },
        onFiltersChange: (newFilters) =>
          onFiltersChange({
            search: newFilters.search,
            bookingType: newFilters.bookingType,
            selectedSpaceGroups: newFilters.spaceGroups,
            showUnassigned: newFilters.unassigned,
            arrival: newFilters.arrival,
            departure: newFilters.departure,
            insurance: newFilters.insurance,
          }),
      }
    : {}

  const table = useDatatable({
    queryKey: ["reservationNightDatatable", marinaSlug, night],
    fetchFn: (params) => getNightDatatable(marinaSlug, night, params),
    defaultFilters: DEFAULT_FILTERS,
    ...datatableFilterConfig,
    fetchAllIds: (filterParams) =>
      getNightSelectableIds(marinaSlug, night, filterParams).then((r) => r.ids),
    enabled: Boolean(night && marinaSlug),
    idKey: "encodedId",
  })

  const { data, isError, error, selection, setSelection } = table
  const reservations = table.records
  const filterOptions = data?.filterOptions || {}

  // Search input state — local value for immediate feedback, debounced for API calls
  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.search || ""
  )
  const [debouncedSetSearch] = useDebounce((value) => {
    if (value) {
      tracker.trackEvent("tonights_boats:filter_applied", {
        filter: "search",
      })
    }
    table.setFilter("search", value)
  })
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  // Modal state
  const [isBulkMessageModalOpen, setIsBulkMessageModalOpen] = useState(false)
  const [singleMessageRowId, setSingleMessageRowId] = useState(null)

  // Track analytics like the legacy component
  useEffect(() => {
    if (showingTonightsBoats && tracker) {
      tracker.trackEvent("tonights_boats:screen_viewed", {
        marina_name: marinaName,
        marina_id: marinaMcomId,
      })
    }
  }, [showingTonightsBoats, marinaName, marinaMcomId, tracker])

  const hasNotes = (row) => {
    return !!(row.specialRequestText || row.reservationNote || row.contactNote)
  }

  const renderCell = (content) => (row) => {
    return typeof content === "function" ? content(row) : content
  }

  const spaceGroupOptions = useMemo(
    () => filterOptions.spaceGroups || [],
    [filterOptions.spaceGroups]
  )
  const unassignedCount = filterOptions.unassignedCount || 0
  const bookingTypeCounts = filterOptions.bookingTypes || {}
  const reservationCount = bookingTypeCounts.reservation ?? 0
  const contractCount = bookingTypeCounts.contract ?? 0

  const assignmentOptions = useMemo(() => {
    const options = []
    options.push({
      value: "unassigned",
      label: `Unassigned (${unassignedCount})`,
      disabled: unassignedCount === 0,
    })
    spaceGroupOptions.forEach((sg) => {
      options.push({
        value: String(sg.id),
        label: `${sg.name} (${sg.count})`,
        disabled: sg.count === 0,
      })
    })
    return options
  }, [spaceGroupOptions, unassignedCount])

  const insuranceOptions = useMemo(() => {
    const statusCounts = filterOptions.insuranceStatuses || {}
    return [
      {
        value: "active",
        label: `Active (${statusCounts.active ?? 0})`,
      },
      {
        value: "expiring_soon",
        label: `Expiring Soon (${statusCounts.expiringSoon ?? 0})`,
      },
      {
        value: "expired",
        label: `Expired (${statusCounts.expired ?? 0})`,
      },
      {
        value: "missing",
        label: `Missing (${statusCounts.missing ?? 0})`,
      },
    ]
  }, [filterOptions.insuranceStatuses])

  const selectedAssignmentValues = useMemo(() => {
    const values = []
    if (table.filters.unassigned) {
      values.push("unassigned")
    }
    table.filters.spaceGroups.forEach((sgId) => {
      values.push(String(sgId))
    })
    return values
  }, [table.filters.unassigned, table.filters.spaceGroups])

  const handleAssignmentChange = (values) => {
    if (values.length > 0) {
      tracker.trackEvent("tonights_boats:filter_applied", {
        filter: "assignment",
      })
    }
    table.setFilters({
      spaceGroups: values.filter((v) => v !== "unassigned"),
      unassigned: values.includes("unassigned"),
    })
  }

  const handleInsuranceChange = (values) => {
    if (values.length > 0) {
      tracker.trackEvent("tonights_boats:filter_applied", {
        filter: "insurance",
      })
    }
    table.setFilter("insurance", values)
  }

  const openBulkMessageModal = () => {
    tracker.trackEvent("tonights_boats:bulk_message_modal_opened", {
      recipient_count: selection.length,
    })
    setSingleMessageRowId(null)
    setIsBulkMessageModalOpen(true)
  }

  const openSingleMessageModal = (row) => {
    tracker.trackEvent("tonights_boats:message_modal_opened", {
      recipient_count: 1,
    })
    setSingleMessageRowId(row.encodedId)
    setIsBulkMessageModalOpen(true)
  }

  const closeBulkMessageModal = () => {
    setIsBulkMessageModalOpen(false)
    setSingleMessageRowId(null)
  }

  const handleBulkExport = async (selectedIds) => {
    if (!selectedIds?.length) return

    tracker.trackEvent("tonights_boats:bulk_export_requested", {
      reservation_count: selectedIds.length,
    })

    try {
      await exportNightReservations({
        marinaSlug,
        night,
        ids: selectedIds,
      })
      tracker.trackEvent("tonights_boats:bulk_export_completed", {
        reservation_count: selectedIds.length,
      })
      setSelection([])
      showToast(`Export is being generated and will be emailed shortly.`, {
        type: "success",
      })
    } catch (_error) {
      tracker.trackEvent("tonights_boats:bulk_export_failed", {
        reservation_count: selectedIds.length,
      })
      showToast("Unable to start export. Please try again.", {
        type: "error",
      })
    }
  }

  const handleBulkMessageSuccess = (messageCount) => {
    tracker.trackEvent("tonights_boats:bulk_message_sent", {
      recipient_count: messageCount,
    })
    setIsBulkMessageModalOpen(false)
    setSelection([])
    showToast(
      `Message sent to ${messageCount} recipient${
        messageCount !== 1 ? "s" : ""
      }`,
      { type: "success" }
    )
  }

  // Build reservation URL from path template
  const getReservationUrl = (row) =>
    paths.reservationPathTemplate.replace("{id}", row.encodedId)

  // Actions for DataTableV2
  const tableActions = [
    ExportAction({ onExport: handleBulkExport }),
    ...(bulkMessagingEnabled
      ? [
          SendMessageAction({
            onClickSingle: openSingleMessageModal,
            onClickBulk: openBulkMessageModal,
          }),
        ]
      : []),
  ]

  const renderBookingType = (row) => (
    <div className="flex flex-col">
      <div>{row.bookingType}</div>
      <div className="text-xs text-gray-600 print:text-xxs">
        #{row.bookingEncodedId}
      </div>
    </div>
  )

  const colDefs = [
    {
      key: "badges",
      header: i18n.headers.tags,
      width: "250px",
      render: (row) => <ReservationBadges reservation={row} />,
    },
    {
      key: "contactName",
      header: i18n.headers.captain,
      width: "180px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => (
        <div className="truncate" title={row.contactName}>
          {row.contactName}
        </div>
      )),
      cardRole: "title",
    },
    {
      key: "boatName",
      header: i18n.headers.boat,
      width: "150px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => <BoatPreviewLink boat={row.boat} />),
    },
    {
      key: "notes",
      header: i18n.headers.notes,
      width: "90px",
      headerAlign: "center",
      cellAlign: "center",
      printHide: true,
      render: (row) => {
        if (!hasNotes(row)) return <span></span>

        return (
          <Tooltip text={renderNoteContent(row)} placement="right">
            <div
              className="flex min-h-[40px] w-full cursor-help items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <i
                className="fa-kit fa-clipboard-pencil text-lg text-blue-600"
                aria-label={i18n.tooltips.viewCustomerNotes}
              />
            </div>
          </Tooltip>
        )
      },
    },
    {
      key: "space",
      header: i18n.headers.assignment,
      width: "160px",
      sortable: true,
      render: (row) => (
        <SpaceAssignmentCell
          reservationId={row.encodedId}
          currentNightSpaceName={row.currentNightSpaceName}
          hasMultipleSpaces={row.hasMultipleSpaces}
          night={night}
        />
      ),
    },
    {
      key: "boatType",
      header: i18n.headers.type,
      width: "90px",
      cellAlign: "left",
      sortable: true,
      hide: true,
      render: renderCell((row) =>
        row.boatType
          ? row.boatType.charAt(0).toUpperCase() + row.boatType.slice(1)
          : ""
      ),
    },
    {
      key: "loa",
      header: i18n.headers.loa,
      width: "85px",
      cellAlign: "left",
      sortable: true,
      hide: true,
      render: renderCell((row) => formatDimension(row.loa)),
    },
    {
      key: "beam",
      header: i18n.headers.beam,
      width: "85px",
      cellAlign: "left",
      sortable: true,
      hide: true,
      render: renderCell((row) => formatDimension(row.beam)),
    },
    {
      key: "draw",
      header: i18n.headers.draft,
      width: "85px",
      cellAlign: "left",
      sortable: true,
      hide: true,
      render: renderCell((row) => formatDimension(row.draw)),
    },
    {
      key: "bookingType",
      header: i18n.headers.booking_type,
      width: "135px",
      cellAlign: "left",
      sortable: false,
      render: renderCell(renderBookingType),
    },
    {
      key: "checkInDate",
      header: i18n.headers.arrival,
      width: "110px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDate(row.checkInDate)),
    },
    {
      key: "checkOutDate",
      header: i18n.headers.departure,
      width: "110px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDate(row.checkOutDate)),
    },
    {
      key: "nights",
      header: i18n.headers.nights,
      width: "85px",
      cellAlign: "left",
      sortable: true,
    },
    {
      key: "electricDisplayName",
      header: i18n.headers.electric,
      width: "85px",
      cellAlign: "left",
      sortable: true,
    },
    {
      key: "insuranceStatus",
      header: "Ins. Status",
      width: "105px",
      cellAlign: "center",
      hide: true,
      render: renderCell((row) =>
        renderInsuranceOrRegistrationStatusIcon(row.insuranceStatus)
      ),
    },
    {
      key: "registrationStatus",
      header: "Reg. Status",
      width: "105px",
      cellAlign: "center",
      hide: true,
      render: renderCell((row) =>
        renderInsuranceOrRegistrationStatusIcon(row.registrationStatus)
      ),
    },
    {
      key: "registrationNumber",
      header: "Reg. Number",
      width: "115px",
      cellAlign: "left",
      hide: true,
    },
    {
      key: "hullIdentification",
      header: i18n.headers.hull_identification,
      width: "130px",
      cellAlign: "left",
      hide: true,
    },
  ]

  const rowConfig = {
    variant: () => "default",
    getViewUrl: getReservationUrl,
    onViewClick: (row, source) => {
      tracker.trackEvent("tonights_boats:reservation_viewed", {
        booking_type: row.bookingType,
        source,
      })
    },
  }

  const renderNoteContent = (row) => {
    const notes = []

    if (row.specialRequestText) {
      notes.push({
        label: i18n.noteLabels.specialRequest,
        content: row.specialRequestText,
      })
    }

    if (row.reservationNote) {
      notes.push({
        label: i18n.noteLabels.reservationNote,
        content: row.reservationNote,
      })
    }

    if (row.contactNote) {
      notes.push({
        label: i18n.noteLabels.customerNote,
        content: row.contactNote,
      })
    }

    return (
      <div className="max-w-sm space-y-2 p-3 text-sm">
        {notes.map((note, index) => (
          <div key={index}>
            <div className="mb-1 font-semibold text-red-600">{note.label}:</div>
            <div className="whitespace-pre-wrap text-gray-800">
              {note.content}
            </div>
            {index < notes.length - 1 && (
              <hr className="my-2 border-gray-200" />
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderAssignmentFilter = () => {
    if (assignmentOptions.length === 0) return null

    return (
      <Select
        multiple
        value={selectedAssignmentValues}
        onValueChange={handleAssignmentChange}
      >
        <SelectTrigger variant="button">Assignment</SelectTrigger>
        <SelectContent>
          {assignmentOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  const bookingTypeOptions = [
    {
      value: BOOKING_TYPE_RESERVATION,
      label: `Reservation (${reservationCount})`,
      disabled: reservationCount === 0,
    },
    {
      value: BOOKING_TYPE_CONTRACT,
      label: `Contract (${contractCount})`,
      disabled: contractCount === 0,
    },
  ]

  const handleBookingTypeSelect = (value) => {
    if (value) {
      tracker.trackEvent("tonights_boats:filter_applied", {
        filter: "booking_type",
      })
    }
    table.setFilter("bookingType", value || "")
  }

  const renderBookingTypeFilter = () => (
    <Select
      value={table.filters.bookingType || null}
      onValueChange={handleBookingTypeSelect}
    >
      <SelectTrigger variant="button">Booking Type</SelectTrigger>
      <SelectContent>
        {bookingTypeOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderInsuranceFilter = () => (
    <Select
      multiple
      value={table.filters.insurance}
      onValueChange={handleInsuranceChange}
    >
      <SelectTrigger variant="button">Insurance</SelectTrigger>
      <SelectContent>
        {insuranceOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderChips = () => {
    const chips = []
    if (table.filters.search) {
      chips.push(
        <Chips.Chip
          key="search"
          text={`Search: ${table.filters.search}`}
          onClick={() => {
            table.clearFilter("search")
            setSearchInputValue("")
          }}
        />
      )
    }
    if (
      [BOOKING_TYPE_RESERVATION, BOOKING_TYPE_CONTRACT].includes(
        table.filters.bookingType
      )
    ) {
      chips.push(
        <Chips.Chip
          key={`booking-type-${table.filters.bookingType}`}
          text={`Booking Type: ${titlecase(table.filters.bookingType)}`}
          onClick={() => table.clearFilter("bookingType")}
        />
      )
    }
    table.filters.insurance.forEach((status) => {
      const labelMap = {
        active: "Active",
        expiring_soon: "Expiring Soon",
        expired: "Expired",
        missing: "Missing",
      }
      chips.push(
        <Chips.Chip
          key={`insurance-${status}`}
          text={`Insurance: ${labelMap[status] || status}`}
          onClick={() => {
            table.setFilter(
              "insurance",
              table.filters.insurance.filter((s) => s !== status)
            )
          }}
        />
      )
    })
    if (table.filters.unassigned) {
      chips.push(
        <Chips.Chip
          key="unassigned"
          text="Unassigned"
          onClick={() => table.clearFilter("unassigned")}
        />
      )
    }
    table.filters.spaceGroups.forEach((sgId) => {
      const sg = spaceGroupOptions.find((s) => String(s.id) === String(sgId))
      if (sg) {
        chips.push(
          <Chips.Chip
            key={`space-group-${sgId}`}
            text={sg.name}
            onClick={() => {
              table.setFilter(
                "spaceGroups",
                table.filters.spaceGroups.filter((id) => id !== sgId)
              )
            }}
          />
        )
      }
    })
    if (chips.length === 0) return null
    return (
      <div className="mb-4 pl-1">
        <Chips>{chips}</Chips>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <div
          className="print:hidden"
          data-testid="reservation-night-table-filters"
        >
          <div className="mb-4 flex w-full flex-wrap items-center gap-2">
            <div className="w-80">
              <Form.IconTextField
                id="night-table-search"
                icon={<i className="fa-kit fa-search-fa" />}
                name="search"
                value={searchInputValue}
                onChange={handleSearchChange}
                placeholder="Search captain, boat, space, or ID"
              />
            </div>
            {renderAssignmentFilter()}
            {renderBookingTypeFilter()}
            {renderInsuranceFilter()}
            {onActiveStatCardChange && (
              <>
                <div className="mx-1 h-6 border-l border-gray-300" />
                <StatCard
                  icon="fa-login"
                  iconBgClass="bg-teal-50"
                  iconColorClass="text-teal-500"
                  value={counts?.arrivals}
                  label="Arrivals"
                  isLoading={countsLoading}
                  isActive={activeStatCard === "arrivals"}
                  onClick={() => {
                    tracker.trackEvent("tonights_boats:stat_card_clicked", {
                      card: "arrivals",
                    })
                    onActiveStatCardChange("arrivals")
                  }}
                />
                <StatCard
                  icon="fa-logout"
                  iconBgClass="bg-yellow-100"
                  iconColorClass="text-yellow-600"
                  value={counts?.departures}
                  label="Departures"
                  isLoading={countsLoading}
                  isActive={activeStatCard === "departures"}
                  onClick={() => {
                    tracker.trackEvent("tonights_boats:stat_card_clicked", {
                      card: "departures",
                    })
                    onActiveStatCardChange("departures")
                  }}
                />
                <StatCard
                  icon="fa-slips"
                  iconBgClass="bg-blue-50"
                  iconColorClass="text-blue-600"
                  value={counts?.tonight}
                  label="Total Boats"
                  isLoading={countsLoading}
                  isActive={activeStatCard === "total"}
                  onClick={() => {
                    tracker.trackEvent("tonights_boats:stat_card_clicked", {
                      card: "total",
                    })
                    onActiveStatCardChange("total")
                  }}
                />
              </>
            )}
          </div>
          <div className="mb-3">
            <InlineNotice
              variant="info"
              icon="fa-export-sheet"
              dismissible
              storagePrefix="tonights_boats"
              storageKey="export_moved_to_bulk_actions_v1"
            >
              Looking for Export? Select rows, then click{" "}
              <strong>Export</strong>.
            </InlineNotice>
          </div>
          <div className="mb-3">
            <InlineNotice
              variant="info"
              icon="fa-print"
              dismissible
              storagePrefix="tonights_boats"
              storageKey="print_moved_to_bulk_actions_v1"
            >
              Looking for Print? Select rows, then click <strong>Print</strong>.
            </InlineNotice>
          </div>
          {renderChips()}
        </div>
        {isError ? (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-center">
            <p className="text-red-800">
              {i18n.errors.loadingMessage.replace(
                "%{error}",
                error?.message || i18n.errors.loadingFallback
              )}
            </p>
          </div>
        ) : (
          <DataTableV2
            name="reservationNightTable"
            columns={colDefs}
            data={reservations}
            actions={tableActions}
            rowConfig={rowConfig}
            rowCount={10}
            maxHeight="none"
            {...table.tableProps}
            selection={selection}
            onSelectionChange={setSelection}
            onPrint={(ids) => {
              const { page, ...printParams } = table.fetchParams
              return printNightReservations({
                marinaSlug,
                night,
                ids,
                ...printParams,
              })
            }}
            renderPrintSubRow={(row, columnCount) => {
              if (!hasNotes(row)) return null

              const notes = []
              if (row.specialRequestText) {
                notes.push({
                  label: i18n.noteLabels.specialRequest,
                  content: row.specialRequestText,
                })
              }
              if (row.reservationNote) {
                notes.push({
                  label: i18n.noteLabels.reservationNote,
                  content: row.reservationNote,
                })
              }
              if (row.contactNote) {
                notes.push({
                  label: i18n.noteLabels.customerNote,
                  content: row.contactNote,
                })
              }

              return (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="bg-gray-50 px-1 py-1 print:text-[7pt]"
                  >
                    <div className="leading-tight">
                      {notes.map((note, index) => (
                        <Fragment key={index}>
                          <span className="font-bold">{note.label}:</span>
                          <span className="ml-1">{note.content}</span>
                          {index < notes.length - 1 && (
                            <span className="mx-2 font-bold">&bull;</span>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </td>
                </tr>
              )
            }}
          />
        )}
      </div>

      <BulkMessageModal
        isOpen={isBulkMessageModalOpen}
        onClose={closeBulkMessageModal}
        onSuccess={handleBulkMessageSuccess}
        selection={singleMessageRowId ? [singleMessageRowId] : selection}
        night={night}
      />
    </div>
  )
}

ReservationNightTable.propTypes = {
  night: PropTypes.string.isRequired,
  showingTonightsBoats: PropTypes.bool,
  marinaName: PropTypes.string,
  marinaMcomId: PropTypes.string,
  bulkMessagingEnabled: PropTypes.bool,
  paths: PropTypes.shape({
    reservationPathTemplate: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    headers: PropTypes.object.isRequired,
    tooltips: PropTypes.object.isRequired,
    noteLabels: PropTypes.object.isRequired,
    print: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
  }).isRequired,
  counts: PropTypes.shape({
    arrivals: PropTypes.number,
    departures: PropTypes.number,
    tonight: PropTypes.number,
  }),
  countsLoading: PropTypes.bool,
  activeStatCard: PropTypes.string,
  onActiveStatCardChange: PropTypes.func,
  filters: PropTypes.shape({
    search: PropTypes.string,
    arrival: PropTypes.bool,
    departure: PropTypes.bool,
    bookingType: PropTypes.string,
    selectedSpaceGroups: PropTypes.arrayOf(PropTypes.string),
    showUnassigned: PropTypes.bool,
    insurance: PropTypes.arrayOf(PropTypes.string),
  }),
  onFiltersChange: PropTypes.func,
}

export default ReservationNightTable
